<script setup lang="ts">
import type { ButtonProps, Form } from '#ui/types';
import { useQueryClient } from '@tanstack/vue-query';
import { z } from 'zod';

import {
    type BackendErrorEntity,
    type ErrorType,
    getApiAuctionDetailQueryKey,
    getApiDirectSaleDetailQueryKey,
    SellingMethod,
    useApiApproveAuction,
    useApiApproveDirectSale,
} from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, type SaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: SaleItemEntity;
}
const { sale } = defineProps<Props>();

// SCHEMA
const depotFormSchema = z
    .object({
        depotTermsConsent: z.boolean(),
        depotVexatiousConsent: z.boolean(),
    })
    .superRefine(({ depotTermsConsent, depotVexatiousConsent }, ctx) => {
        if (!sale.includesDepotService) {
            return;
        }

        if (!depotTermsConsent)
            ctx.addIssue({
                code: 'custom',
                path: ['depotTermsConsent'],
                message: tt('sdp.draftConfirmation.modal.content.depot.validation.mandatoryConsent'),
            });

        if (!depotVexatiousConsent)
            ctx.addIssue({
                code: 'custom',
                path: ['depotVexatiousConsent'],
                message: tt('sdp.draftConfirmation.modal.content.depot.validation.mandatoryConsent'),
            });
    });
const formSchema = z.object({}).extend(depotFormSchema.shape);

type FormSchema = typeof formSchema;

export type FormData = z.infer<FormSchema>;

// COMPOSABLES
const { id } = useSdpParams();
const { isLoggedIn, getSession, data: session } = useAuthUtils();
const { tt } = useTypedI18n();
const { logError } = useLogs();
const { notifySuccess, notifyBackendError, notifyGenericError } = useNotification();
const queryClient = useQueryClient();
const { isMobile } = useResponsive();
const { openLoginModal } = useModals();
const { hasToBeApproved } = useSdpToBeApproved(toRef(() => sale));
const { trackAdpConfirmButton, trackDsdpConfirmButton } = useTracking();
const { mutateAsync: mutateApproveAuctionDraft, isPending: isApprovingAuctionDraft } = useApiApproveAuction();
const { mutateAsync: mutateApproveDirectSaleDraft, isPending: isApprovingDirectSaleDraft } = useApiApproveDirectSale();

// DATA
const vexatiousClauses = useStoryblokVexatiousClauses({ slug: ROUTE_NAMES.DEPOT_TERMS_AND_CONDITIONS, enabled: () => sale.includesDepotService });
const form = useTemplateRef<Form<FormSchema>>('form');
const formData = reactive<FormData>({
    depotTermsConsent: false,
    depotVexatiousConsent: false,
});
const open = ref<boolean>(false);
const actions = computed<ButtonProps[]>(() => [
    {
        label: isLoggedIn.value ? tt('sdp.draftConfirmation.action') : tt('sdp.draftConfirmation.loginAction'),
        color: 'neutral',
        variant: 'outline',
        size: 'lg',
        class: 'text-primary ring-0 justify-center',
        icon: 'lucide:check-line',
        onClick: async (): Promise<void> => {
            if (!isLoggedIn.value) {
                if (!(await openLoginModal())) return;
            }

            await getSession();

            if (sale?.sellerCompanyId !== session.value?.user?.companyId) {
                notifyGenericError();

                return;
            }

            open.value = true;
        },
        loading: isAuction(sale) ? isApprovingAuctionDraft.value : isApprovingDirectSaleDraft.value,
    },
]);

// METHODS
const onSubmit = (): void => {
    if (sale.sellingMethod === SellingMethod.Auction) trackAdpConfirmButton();
    if (sale.sellingMethod === SellingMethod.DirectSale) trackDsdpConfirmButton();

    const data = sale.includesDepotService ? { depotTermsAccepted: true } : {};

    (isAuction(sale) ? mutateApproveAuctionDraft({ auctionId: sale.id, data }) : mutateApproveDirectSaleDraft({ directSaleId: sale.id, data }))
        .then(data => {
            notifySuccess({
                title: tt('sdp.draftConfirmation.success.title'),
                description: tt('sdp.draftConfirmation.success.description'),
            });
            queryClient.setQueryData(isAuction(sale) ? getApiAuctionDetailQueryKey(id) : getApiDirectSaleDetailQueryKey(id), data);
        })
        .catch((apiError: ErrorType<BackendErrorEntity>): void => {
            logError('Error confirm auction draft', { error: apiError });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
        })
        .finally(() => (open.value = false));
};
</script>

<template>
    <BaseToolbarAlert
        v-if="hasToBeApproved"
        :title="tt('sdp.draftConfirmation.title')"
        :description="tt('sdp.draftConfirmation.description')"
        :actions="actions"
        class="text-inverted"
        :class="{ 'bg-primary': isLoggedIn, 'bg-neutral-500': !isLoggedIn }"
        icon="lucide:view"
    />
    <UModal
        v-model:open="open"
        :fullscreen="isMobile"
        :title="tt('sdp.draftConfirmation.modal.title')"
        :description="tt('sdp.draftConfirmation.modal.description')"
    >
        <template #body>
            <UForm ref="form" class="space-y-4" :schema="formSchema" :state="formData" @submit="onSubmit">
                <p class="text-toned text-sm">{{ tt('sdp.draftConfirmation.modal.content.base') }}</p>
                <UForm v-if="sale.includesDepotService" class="space-y-4" :schema="depotFormSchema" nested>
                    <BaseAlert
                        type="info"
                        :title="tt('sdp.draftConfirmation.modal.content.depot.notice.title')"
                        :description="tt('sdp.draftConfirmation.modal.content.depot.notice.description')"
                    />
                    <UFormField name="depotTermsConsent">
                        <UCheckbox v-model="formData.depotTermsConsent" aria-required="true">
                            <template #label>
                                <BaseI18nT tag="span" keypath="sdp.draftConfirmation.modal.content.depot.termsConsent.base">
                                    <template #link>
                                        <NuxtLinkLocale
                                            class="text-primary font-semibold underline"
                                            :to="ROUTE_NAMES.DEPOT_TERMS_AND_CONDITIONS"
                                            target="_blank"
                                        >
                                            {{ tt('sdp.draftConfirmation.modal.content.depot.termsConsent.link') }}
                                        </NuxtLinkLocale>
                                    </template>
                                </BaseI18nT>
                            </template>
                        </UCheckbox>
                    </UFormField>
                    <UFormField name="depotVexatiousConsent">
                        <UCheckbox v-model="formData.depotVexatiousConsent" aria-required="true">
                            <template #label>
                                {{ tt('sdp.draftConfirmation.modal.content.depot.vexatiousClausesConsent') }}
                                <p v-if="vexatiousClauses" class="text-muted mt-2 max-h-32 overflow-y-scroll text-xs leading-4 font-normal">
                                    {{ vexatiousClauses }}
                                </p>
                            </template>
                        </UCheckbox>
                    </UFormField>
                </UForm>
            </UForm>
        </template>
        <template #footer="{ close }">
            <UButton
                size="xl"
                color="primary"
                :loading="isApprovingAuctionDraft || isApprovingDirectSaleDraft"
                :disabled="isApprovingAuctionDraft || isApprovingDirectSaleDraft"
                :label="tt('sdp.draftConfirmation.modal.approve')"
                block
                @click="form?.submit()"
            />
            <UButton size="xl" color="neutral" variant="link" :label="tt('sdp.draftConfirmation.modal.cancel')" block @click="close" />
        </template>
    </UModal>
</template>
